import React, { useState, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { quizzes as quizBank, MONTHLY_QUIZ_REQUIREMENT } from '../utils/quizData';
import { format } from 'date-fns';
import {
  BookOpen, CheckCircle, AlertTriangle, Users, ChevronDown, ChevronUp,
  Award, Clock, Target
} from 'lucide-react';
import './Quizzes.css';

function Quizzes() {
  const { state } = useApp();
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [expandedEmployee, setExpandedEmployee] = useState(null);

  // Merge code-defined quizzes with any Firestore quizzes
  const allQuizzes = useMemo(() => {
    const firestoreIds = new Set(state.quizzes.map(q => q.id));
    const merged = [...state.quizzes];
    quizBank.forEach(q => {
      if (!firestoreIds.has(q.id)) merged.push(q);
    });
    return merged;
  }, [state.quizzes]);

  const [year, month] = selectedMonth.split('-').map(Number);

  // Build employee quiz summary for selected month
  const employeeSummary = useMemo(() => {
    const activeEmployees = state.employees.filter(e => e.status === 'active');
    return activeEmployees.map(emp => {
      const attempts = state.quizAttempts.filter(a => {
        if (a.employeeId !== emp.id) return false;
        const d = new Date(a.submittedAt);
        return d.getFullYear() === year && d.getMonth() + 1 === month;
      });
      const passed = attempts.filter(a => a.passed);
      const avgScore = attempts.length > 0
        ? Math.round(attempts.reduce((sum, a) => sum + a.score, 0) / attempts.length)
        : null;

      return {
        employee: emp,
        attempts,
        passed,
        totalAttempts: attempts.length,
        passedCount: passed.length,
        avgScore,
        metRequirement: passed.length >= MONTHLY_QUIZ_REQUIREMENT
      };
    }).sort((a, b) => b.passedCount - a.passedCount);
  }, [state.employees, state.quizAttempts, year, month]);

  const totalEmployees = employeeSummary.length;
  const metRequirement = employeeSummary.filter(e => e.metRequirement).length;
  const totalAttempts = employeeSummary.reduce((sum, e) => sum + e.totalAttempts, 0);

  // Month selector options
  const monthOptions = useMemo(() => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 6; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      options.push({
        value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
        label: format(d, 'MMMM yyyy')
      });
    }
    return options;
  }, []);

  const getQuizTitle = (quizId) => {
    const q = allQuizzes.find(q => q.id === quizId);
    return q?.title || quizId;
  };

  return (
    <div className="quizzes-page">
      <div className="quizzes-header">
        <div>
          <h1>Quizzes & Training</h1>
          <p className="quizzes-subtitle">
            Monthly requirement: {MONTHLY_QUIZ_REQUIREMENT} quizzes passed per team member
          </p>
        </div>
        <select
          className="quizzes-month-select"
          value={selectedMonth}
          onChange={e => setSelectedMonth(e.target.value)}
        >
          {monthOptions.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {/* Summary Cards */}
      <div className="quizzes-stats">
        <div className="quizzes-stat-card">
          <div className="quizzes-stat-icon"><BookOpen size={20} /></div>
          <div className="quizzes-stat-info">
            <span className="quizzes-stat-value">{allQuizzes.filter(q => q.status === 'active').length}</span>
            <span className="quizzes-stat-label">Active Quizzes</span>
          </div>
        </div>
        <div className="quizzes-stat-card">
          <div className="quizzes-stat-icon"><Target size={20} /></div>
          <div className="quizzes-stat-info">
            <span className="quizzes-stat-value">{totalAttempts}</span>
            <span className="quizzes-stat-label">Total Attempts</span>
          </div>
        </div>
        <div className="quizzes-stat-card">
          <div className="quizzes-stat-icon"><Users size={20} /></div>
          <div className="quizzes-stat-info">
            <span className="quizzes-stat-value">{metRequirement}/{totalEmployees}</span>
            <span className="quizzes-stat-label">Met Requirement</span>
          </div>
        </div>
      </div>

      {/* Available Quizzes */}
      <div className="quizzes-section">
        <h2>Available Quizzes</h2>
        <div className="quizzes-list">
          {allQuizzes.filter(q => q.status === 'active').map(quiz => (
            <div key={quiz.id} className="quiz-card">
              <div className="quiz-card-header">
                <BookOpen size={18} />
                <span className="quiz-card-title">{quiz.title}</span>
                <span className={`quiz-difficulty ${quiz.difficulty}`}>{quiz.difficulty}</span>
              </div>
              <p className="quiz-card-desc">{quiz.description}</p>
              <div className="quiz-card-meta">
                <span><Clock size={14} /> {quiz.timeEstimate}</span>
                <span><Target size={14} /> {quiz.passingScore}% to pass</span>
                <span><BookOpen size={14} /> {quiz.questions.length} questions</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Employee Progress Table */}
      <div className="quizzes-section">
        <h2>Team Progress</h2>
        {employeeSummary.length === 0 ? (
          <div className="quizzes-empty">
            <p>No active employees found.</p>
          </div>
        ) : (
          <div className="quizzes-progress-table">
            <div className="quizzes-table-header">
              <span className="qt-col-name">Employee</span>
              <span className="qt-col-num">Passed</span>
              <span className="qt-col-num">Attempts</span>
              <span className="qt-col-num">Avg Score</span>
              <span className="qt-col-status">Status</span>
            </div>
            {employeeSummary.map(({ employee, attempts, passedCount, totalAttempts: ta, avgScore, metRequirement: met }) => (
              <div key={employee.id} className="quizzes-table-row-wrapper">
                <div
                  className={`quizzes-table-row ${expandedEmployee === employee.id ? 'expanded' : ''}`}
                  onClick={() => setExpandedEmployee(expandedEmployee === employee.id ? null : employee.id)}
                >
                  <span className="qt-col-name">
                    {employee.firstName} {employee.lastName}
                    <span className="qt-role">{employee.role}</span>
                  </span>
                  <span className="qt-col-num">{passedCount}/{MONTHLY_QUIZ_REQUIREMENT}</span>
                  <span className="qt-col-num">{ta}</span>
                  <span className="qt-col-num">{avgScore !== null ? `${avgScore}%` : '—'}</span>
                  <span className="qt-col-status">
                    {met ? (
                      <span className="qt-badge met"><CheckCircle size={14} /> Complete</span>
                    ) : ta > 0 ? (
                      <span className="qt-badge partial"><Clock size={14} /> In Progress</span>
                    ) : (
                      <span className="qt-badge none"><AlertTriangle size={14} /> Not Started</span>
                    )}
                    {expandedEmployee === employee.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </span>
                </div>
                {expandedEmployee === employee.id && (
                  <div className="quizzes-table-detail">
                    {attempts.length === 0 ? (
                      <p className="qt-detail-empty">No quiz attempts this month.</p>
                    ) : (
                      <div className="qt-detail-list">
                        {attempts.map(att => (
                          <div key={att.id} className="qt-detail-item">
                            <Award size={14} />
                            <span className="qt-detail-title">{getQuizTitle(att.quizId)}</span>
                            <span className={`qt-detail-score ${att.passed ? 'pass' : 'fail'}`}>
                              {att.score}%
                            </span>
                            <span className="qt-detail-date">
                              {format(new Date(att.submittedAt), 'MMM d, h:mm a')}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Quizzes;
